/**
 * Email Journal PDF — CORE builder (ADR-074 §3.5 req 8, Stage 6).
 *
 * The OUTBOUND email-observability surface's "Export" attachment: a one-document
 * snapshot of the email journeys currently visible on the Email Validation tab
 * (the same filter/search/sort the operator is looking at). Built for an operator
 * to attach to a support ticket or hand to AWS when an email delivery is
 * disputed — "here is exactly what we sent, when, and what SES told us".
 *
 * Follows the existing builders in `fix-guide-pdf-core.ts` verbatim in shape:
 * the jsPDF constructor is dependency-injected (each consumer resolves jspdf from
 * its own node_modules), the document surface is the shared structural
 * {@link PdfDocLike}, and the branded {@link headerBand} + {@link makeWrap}
 * primitives are re-used so the brand treatment can never diverge from the Fix
 * Guide reports.
 *
 * Honesty rule (§3.3) travels into the export: a delivered-but-unread flow shows
 * its server-derived state label ("Delivered — no read receipt returned") — never
 * a confident "unread" — exactly as the surface renders it.
 */
import {
  headerBand,
  makeWrap,
  type Cursor,
  type PdfDocLike,
  type PdfDocFactory,
} from './fix-guide-pdf-core';

// Re-export the shared PDF surface types so consumers/tests can import them
// alongside the email-journal builder from one module path.
export type { PdfDocLike, PdfDocFactory } from './fix-guide-pdf-core';

/** One transition rectangle on a journey's flow row (§11). */
export interface EmailJournalTransition {
  stage: string;
  /** ISO-8601. */
  at: string;
  /** A re-send is a repeat `Sent` carrying this marker (drives "↺ Resent"). */
  resent?: boolean;
}

/**
 * One outbound email journey row, flattened for the PDF. Mirrors the visible
 * surface row (the adapter maps `OutboundEmailRow` → this); the core stays free
 * of the lambda/portal types so it can live in portal-shared.
 */
export interface EmailJournalEntry {
  friendlyId: string;
  recipient: string;
  fromAddress: string;
  workflow: string;
  subject: string;
  sesMessageId: string | null;
  /** ISO-8601 of the first Sent transition, or null if never sent. */
  sentAt: string | null;
  /** ISO-8601 of the last recorded event. */
  lastEventAt: string;
  /** The server-derived display state label (honours the §3.3 rule verbatim). */
  stateLabel: string;
  /** True when this flow is a problem the operator should look at. */
  isIssue: boolean;
  /** The category label used for the summary tally (e.g. "Delivered", "Bounced"). */
  category: string;
  transitions: EmailJournalTransition[];
}

export interface EmailJournalData {
  /** Full day date, never month-only (universal rule). e.g. "24 June 2026". */
  generatedDate: string;
  /** What the export is scoped to — the active filter/search description. */
  scopeLabel: string;
  /** The visible journeys, in the surface's current sort order. */
  entries: EmailJournalEntry[];
}

// ── Category colours (mirror the surface's issue-oriented tones) ─────────────
const ISSUE_RGB: [number, number, number] = [220, 38, 38]; // red — bounced/complaint/rejected/limbo
const OK_RGB: [number, number, number] = [22, 163, 74]; // green — delivered/closed/read
const AWAIT_RGB: [number, number, number] = [180, 83, 9]; // amber — sent/queued/awaiting

/** The fixed tally order for the summary line (issues first, then healthy). */
const CATEGORY_ORDER = [
  'Bounced',
  'Complaint',
  'Rejected',
  'In Limbo',
  'Sent',
  'Delivered',
  'Closed',
] as const;

function categoryRgb(category: string, isIssue: boolean): [number, number, number] {
  if (isIssue) return ISSUE_RGB;
  if (category === 'Sent' || category === 'Queued') return AWAIT_RGB;
  return OK_RGB;
}

// ── Timestamp helpers (mirror EmailValidationOutbound — UTC-stable slicing) ──
function fmtFull(iso: string | null): string {
  if (!iso) return '—';
  const m = /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]} ${m[4]}:${m[5]}:${m[6]}` : iso;
}
function fmtDelta(fromIso: string | null, toIso: string): string {
  if (!fromIso) return fmtFull(toIso);
  const ms = Date.parse(toIso) - Date.parse(fromIso);
  if (Number.isNaN(ms) || ms < 0) return '—';
  const totalSec = Math.round(ms / 1000);
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  return `+${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/** Render one journey's transition flow as a single text line ("Sent 24/06 → Delivered +00:03"). */
function flowLine(entry: EmailJournalEntry): string {
  const sentAt = entry.sentAt ?? entry.transitions[0]?.at ?? entry.lastEventAt;
  let resends = 0;
  const parts = entry.transitions.map((t, i) => {
    if (t.resent) {
      resends += 1;
      return `↺ Resent (${resends}) ${fmtDelta(sentAt, t.at)}`;
    }
    const label = t.stage === 'InLimbo' ? 'In Limbo' : t.stage;
    const when = i === 0 ? fmtFull(t.at) : fmtDelta(sentAt, t.at);
    return `${label} ${when}`;
  });
  return parts.length > 0 ? parts.join('  →  ') : 'Queued (no transitions yet)';
}

/**
 * Build the Email Journal export. Pure — the jsPDF constructor is injected.
 * Page 1 opens with the branded header + a state tally over the FULL visible set,
 * then lists every visible journey (newest-first by the caller's sort) with its
 * metadata + the dated flow line. Returns the document for the caller to output.
 */
export function buildEmailJournalPdfCore(createDoc: PdfDocFactory, data: EmailJournalData): PdfDocLike {
  const doc = createDoc();
  const W = 210;
  const M = 16;
  const cur: Cursor = { y: 20 };
  const wrap = makeWrap(doc, cur, W, M);

  headerBand(
    doc, W, M,
    'Email Journal — Outbound',
    `${data.entries.length} flow${data.entries.length === 1 ? '' : 's'}  ·  Generated ${data.generatedDate}  ·  Nexanomics Email Observability`,
  );

  // ── Summary tally (full visible set) ──
  cur.y = 42;
  wrap('Outbound email journeys', 16, 'bold', [15, 23, 42], M);
  cur.y += 2;
  wrap(`Scope: ${data.scopeLabel}`, 9, 'italic', [100, 116, 139], M);
  cur.y += 3;

  const counts = new Map<string, number>();
  let issues = 0;
  for (const e of data.entries) {
    counts.set(e.category, (counts.get(e.category) ?? 0) + 1);
    if (e.isIssue) issues += 1;
  }

  // Tally line — issues stand out; zeros are omitted (the export is a snapshot,
  // not a fixed dashboard, so an absent category simply isn't listed).
  doc.setFontSize(10);
  let lx = M;
  const tallyY = cur.y;
  for (const cat of CATEGORY_ORDER) {
    const n = counts.get(cat);
    if (!n) continue;
    const isIssueCat = cat === 'Bounced' || cat === 'Complaint' || cat === 'Rejected' || cat === 'In Limbo';
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...categoryRgb(cat, isIssueCat));
    const label = `${cat} ${n}`;
    if (lx + doc.getTextWidth(label) > W - M) { lx = M; cur.y += 6; }
    doc.text(label, lx, cur.y);
    lx += doc.getTextWidth(label) + 8;
  }
  if (counts.size === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(148, 163, 184);
    doc.text('No journeys in the current view.', M, tallyY);
  }
  cur.y += 8;

  // A one-line issue call-out so the recipient sees breakage at a glance.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...(issues > 0 ? ISSUE_RGB : OK_RGB));
  doc.text(
    issues > 0
      ? `${issues} flow${issues === 1 ? ' needs' : 's need'} attention (bounced, complaint, rejected, or in limbo).`
      : 'No delivery issues in this view.',
    M, cur.y,
  );
  cur.y += 8;

  // ── Per-journey detail ──
  data.entries.forEach((e) => {
    if (cur.y > 262) { doc.addPage(); cur.y = 20; }
    const yStart = cur.y - 4;
    doc.setDrawColor(...(e.isIssue ? ISSUE_RGB : [99, 102, 241] as [number, number, number]));
    doc.setLineWidth(0.8);

    // Title row: friendly ID + workflow + state.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text(e.friendlyId, M + 4, cur.y);
    const idW = doc.getTextWidth(e.friendlyId);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`· ${e.workflow}`, M + 4 + idW + 3, cur.y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...categoryRgb(e.category, e.isIssue));
    const stateText = e.stateLabel;
    doc.text(stateText, W - M - doc.getTextWidth(stateText), cur.y);
    cur.y += 5;

    wrap(`To: ${e.recipient}    From: ${e.fromAddress}`, 9, 'normal', [51, 65, 85], M + 4);
    wrap(`Subject: ${e.subject}`, 9, 'normal', [51, 65, 85], M + 4);
    if (e.sesMessageId) wrap(`SES MessageId: ${e.sesMessageId}`, 8, 'normal', [148, 163, 184], M + 4);
    wrap(`Sent: ${fmtFull(e.sentAt)}    Last event: ${fmtFull(e.lastEventAt)}`, 8, 'normal', [100, 116, 139], M + 4);
    wrap(flowLine(e), 9, 'normal', [67, 56, 202], M + 4);

    cur.y += 2;
    doc.setDrawColor(...(e.isIssue ? ISSUE_RGB : [99, 102, 241] as [number, number, number]));
    doc.line(M, yStart, M, cur.y - 2);
    cur.y += 6;
  });

  if (data.entries.length === 0) {
    wrap('Nothing to export — no journeys match the current filter/search.', 10, 'italic', [148, 163, 184], M);
  }

  return doc;
}
